import React, { useState, useEffect } from 'react';
import { Container, Box, useTheme } from '@mui/material';
import LoginForm from './LoginForm';
import RegisterForm from './RegisterForm';
import PasswordResetForm from './PasswordResetForm';
import VerificationForm from './VerificationForm';
import { useAuth } from '../../context/AuthContext';
import { updatePageSEO } from '../../utils/seo';

const AuthPage = () => {
  const { verifyRegistration, verifyMFA, resendCode } = useAuth();
  const [currentForm, setCurrentForm] = useState('login'); // 'login', 'register', 'reset', 'verify_registration', 'verify_mfa'
  const [verifyData, setVerifyData] = useState({ email: '', devCode: '', mfaType: 'email' });

  const theme = useTheme();

  useEffect(() => {
    switch (currentForm) {
      case 'register':
        updatePageSEO('Kostenlos Registrieren', 'Erstelle dein kostenloses Konto bei NebenanHilfe.de und vernetze dich mit Nachbarn in deinem Quartier.');
        break;
      case 'reset':
        updatePageSEO('Passwort zurücksetzen', 'Setze dein Passwort sicher zurück und stelle den Zugriff auf dein Nachbarschaftskonto wieder her.');
        break;
      case 'verify_registration':
      case 'verify_mfa':
        updatePageSEO('Sicherheitsüberprüfung', 'Bestätige deinen Anmeldecode oder deine 2FA-Authenticator-App für maximalen Kontoschutz.');
        break;
      case 'login':
      default:
        updatePageSEO('Anmelden', 'Melde dich bei NebenanHilfe.de an, um Hilfsangebote, Gesuche und den lokalen Marktplatz zu nutzen.');
        break;
    }
  }, [currentForm]);

  const handleRequireVerification = (email, devCode) => {
    setVerifyData({ email, devCode, mfaType: 'email' });
    setCurrentForm('verify_registration');
  };

  const handleRequireMFA = (email, devCode, mfaType = 'email') => {
    setVerifyData({ email, devCode, mfaType: mfaType || 'email' });
    setCurrentForm('verify_mfa');
  };

  const handleVerifyRegistration = async (code) => {
    return await verifyRegistration(verifyData.email, code);
  };

  const handleVerifyMFA = async (code, type) => {
    return await verifyMFA(verifyData.email, code, type || verifyData.mfaType);
  };

  const handleResendCode = async (type) => {
    return await resendCode(verifyData.email, type);
  };

  const renderForm = () => {
    switch (currentForm) {
      case 'register':
        return (
          <RegisterForm 
            onSwitchToLogin={() => setCurrentForm('login')}
            onRequireVerification={handleRequireVerification}
            onRequireMFA={handleRequireMFA}
          />
        );
      case 'reset':
        return (
          <PasswordResetForm 
            onSwitchToLogin={() => setCurrentForm('login')}
          />
        );
      case 'verify_registration':
        return (
          <VerificationForm
            email={verifyData.email}
            type="registration"
            devCode={verifyData.devCode}
            onVerify={handleVerifyRegistration}
            onResend={() => handleResendCode('verification')}
            onCancel={() => setCurrentForm('login')}
          />
        );
      case 'verify_mfa':
        return (
          <VerificationForm
            email={verifyData.email}
            type="mfa"
            mfaType={verifyData.mfaType}
            devCode={verifyData.devCode}
            onVerify={handleVerifyMFA}
            onResend={() => handleResendCode('mfa')}
            onCancel={() => setCurrentForm('login')}
          />
        );
      default:
        return (
          <LoginForm 
            onSwitchToRegister={() => setCurrentForm('register')}
            onSwitchToReset={() => setCurrentForm('reset')}
            onRequireVerification={handleRequireVerification}
            onRequireMFA={handleRequireMFA}
          />
        );
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: theme.palette.mode === 'light'
          ? 'radial-gradient(circle at 15% 20%, rgba(37, 99, 235, 0.07) 0%, transparent 45%), radial-gradient(circle at 85% 80%, rgba(15, 118, 110, 0.06) 0%, transparent 45%), #f8fafc'
          : 'radial-gradient(circle at 15% 20%, rgba(59, 130, 246, 0.15) 0%, transparent 50%), radial-gradient(circle at 85% 80%, rgba(13, 148, 136, 0.1) 0%, transparent 50%), #0b0f19',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="sm">
        <Box sx={{ animation: 'fadeIn 0.6s ease-out' }}>
          {renderForm()}
        </Box>
      </Container>
    </Box>
  );
};

export default AuthPage;
